import React from 'react';
import { Typography } from '@fullstory/components';
import cssColors from './index.color.module.scss';
import css, { Styles } from './index.module.scss';
import { classNames } from '@fullstory/util/fmt';

export interface EngagementMapsRankIndicatorProps {
  /**
   * The rank of this indicator (e.g. 0 or 9). The rank is 0-based but is displayed as 1-based.
   */
  rank: number;
  /**
   * The primary color of the indicator.
   */
  color: keyof typeof cssColors;
  /**
   * The size of the indicator (both the width and height).
   */
  size: 24 | 36 | 48;
  /**
   * Whether the indicator is currently active or inactive.
   */
  active?: boolean;
  /**
   * Whether the rank indicator should ignore clicks colors and make everything easier to see.
   */
  heatmapMode?: boolean;
}

/**
 * A component for displaying the rank of an item relative to another.
 */
export const EngagementMapsRankIndicator: React.FC<EngagementMapsRankIndicatorProps> = ({
  rank,
  color,
  size,
  active = false,
  heatmapMode = false,
}) => {
  function convertSizeToTypographyVariant(size: number): 'heading1' | 'heading3' | 'heading5' {
    switch (size) {
      case 48:
        return 'heading1';
      case 36:
        return 'heading3';
      case 24:
      default:
        return 'heading5';
    }
  }

  return (
    <div
      data-component="EngagementMapsRankIndicator"
      data-fs="engagement-maps-rank-indicator"
      data-rank={rank}
      data-color={color}
      data-active={active}
      className={classNames(
        css.rankIndicator,
        css[`size_${size}px` as keyof Styles],
        { [cssColors[color ?? 'blue']]: active },
        { [css.inactive]: !active },
        { [css.heatmap]: heatmapMode },
      )}
    >
      <Typography
        variant={convertSizeToTypographyVariant(size)}
        color={active ? 'neutrals-n0' : color}
        lineHeight={`${size}px`}
      >
        {rank + 1}
      </Typography>
    </div>
  );
};
